import { cn } from "@/v5/lib/utils";
import { scrollAncestorIntoViewX } from "@/v5/lib/scrollWithin";
import {
  Filter,
  HeartPulse,
  Mail,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Phone,
  StickyNote,
  Video,
} from "lucide-react";

export type FilterChannel =
  | "sms"
  | "email"
  | "call"
  | "whatsapp"
  | "video"
  | "note"
  | "healthnexus"
  | "marketing";

const FILTER_PILLS: { value: FilterChannel; label: string; icon: typeof Phone }[] = [
  { value: "call", label: "Call", icon: Phone },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { value: "email", label: "Email", icon: Mail },
  { value: "video", label: "Video", icon: Video },
  { value: "note", label: "Note", icon: StickyNote },
  { value: "marketing", label: "Marketing", icon: Megaphone },
  { value: "healthnexus", label: "HealthNexus", icon: HeartPulse },
];

const PILL_COLORS: Record<FilterChannel, { idle: string; active: string }> = {
  sms: {
    idle: "text-slate-500 hover:bg-blue-50 hover:text-blue-600",
    active: "bg-blue-100 text-blue-700 ring-1 ring-blue-300",
  },
  email: {
    idle: "text-slate-500 hover:bg-orange-50 hover:text-orange-600",
    active: "bg-orange-100 text-orange-700 ring-1 ring-orange-300",
  },
  call: {
    idle: "text-slate-500 hover:bg-green-50 hover:text-green-600",
    active: "bg-green-100 text-green-700 ring-1 ring-green-300",
  },
  whatsapp: {
    idle: "text-slate-500 hover:bg-emerald-50 hover:text-emerald-600",
    active: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300",
  },
  video: {
    idle: "text-slate-500 hover:bg-violet-50 hover:text-violet-600",
    active: "bg-violet-100 text-violet-700 ring-1 ring-violet-300",
  },
  note: {
    idle: "text-slate-500 hover:bg-yellow-50 hover:text-yellow-600",
    active: "bg-yellow-100 text-yellow-700 ring-1 ring-yellow-300",
  },
  healthnexus: {
    idle: "text-slate-500 hover:bg-cyan-50 hover:text-cyan-600",
    active: "bg-cyan-100 text-cyan-700 ring-1 ring-cyan-300",
  },
  marketing: {
    idle: "text-slate-500 hover:bg-teal-50 hover:text-teal-600",
    active: "bg-teal-100 text-teal-700 ring-1 ring-teal-300",
  },
};

interface ChannelFilterPillsProps {
  activeChannels: Set<FilterChannel>;
  onChange: (channels: Set<FilterChannel>) => void;
  /** Hide specific channels from the filter row. */
  exclude?: FilterChannel[];
  className?: string;
}

export function ChannelFilterPills({
  activeChannels,
  onChange,
  exclude,
  className,
}: ChannelFilterPillsProps) {
  const pills = exclude
    ? FILTER_PILLS.filter((p) => !exclude.includes(p.value))
    : FILTER_PILLS;

  const allActive = activeChannels.size === 0 || activeChannels.size === pills.length;

  /**
   * SCRUM-1428 asked that "the selected option should scroll into view automatically".
   * The row scrolls (`max-lg:overflow-x-auto` in ConversationThread) but nothing ever
   * moved it, so choosing one of the trailing pills — Marketing, Health Nexus, System
   * Messages — left the thing you had just selected off-screen.
   *
   * Scoped to the nearest scroller rather than `scrollIntoView`, which would also drag
   * the chart shell and the document sideways. That is the defect behind nine other
   * tickets in this effort; see scrollWithin's docblock.
   */
  function revealPill(el: HTMLElement | null) {
    if (el) scrollAncestorIntoViewX(el, true);
  }

  function toggle(channel: FilterChannel) {
    const next = new Set(activeChannels);
    if (next.has(channel)) {
      next.delete(channel);
    } else {
      next.add(channel);
    }
    if (next.size === pills.length) {
      onChange(new Set());
    } else {
      onChange(next);
    }
  }

  function clearAll() {
    onChange(new Set());
  }

  return (
    <div
      className={cn(
        // Nine labelled pills overflow every column narrower than the ≥1024 one.
        // The scroll container is the filter row in ConversationThread, this
        // component's only call site — `shrink-0` here hands the pills' full
        // natural width to that scroller instead of compressing to fit.
        "flex items-center gap-1 border-b border-slate-100 bg-slate-50/60 px-3 py-1.5 max-lg:shrink-0",
        className,
      )}
    >
      <Filter className="mr-0.5 h-3 w-3 shrink-0 text-slate-400" />
      <button
        type="button"
        onClick={clearAll}
        className={cn(
          "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors max-lg:shrink-0",
          allActive
            ? "bg-slate-200 text-slate-700"
            : "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
        )}
      >
        All
      </button>
      {pills.map((pill) => {
        const Icon = pill.icon;
        const isActive = activeChannels.has(pill.value);
        const colors = PILL_COLORS[pill.value];
        return (
          <button
            key={pill.value}
            type="button"
            onClick={(e) => {
              toggle(pill.value);
              revealPill(e.currentTarget);
            }}
            className={cn(
              "flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors max-lg:shrink-0 max-lg:whitespace-nowrap",
              isActive ? colors.active : colors.idle,
            )}
          >
            <Icon className="h-3 w-3" />
            {pill.label}
          </button>
        );
      })}
    </div>
  );
}
